import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import Seo from "@/components/Seo";

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/insights").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative">
      <Seo title="Insights" description="Execution playbooks, market-structure breakdowns and product notes from the HK Forexedge trading desk." />
      <section className="mx-auto max-w-7xl px-5 pt-16 pb-10 lg:px-8 lg:pt-24">
        <p className="overline mb-3">Insights</p>
        <h1 data-testid="insights-title" className="font-editorial text-4xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-5xl lg:text-6xl">
          Notes from the desk.
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-[color:var(--ink-body)]">
          Execution playbooks, market structure breakdowns, and product notes — written by the traders building the tooling.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        {loading ? (
          <div className="card-edge p-8 text-[color:var(--ink-muted)]">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="card-edge p-8 text-[color:var(--ink-muted)]" data-testid="insights-empty">
            No articles published yet. Check back soon.
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
                <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`} className="card-edge group flex h-full flex-col overflow-hidden hover:-translate-y-1">
                  <div className="photo-vignette h-44 rounded-none border-0">
                    <img src={p.cover_image || IMG.charts} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {p.video_url && (
                      <span className="absolute left-3 top-3 chip chip-amber"><Play size={11} /> Video</span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-editorial text-xl font-semibold tracking-tight text-[color:var(--ink)]">{p.title}</h3>
                    <p className="mt-2 line-clamp-3 text-sm text-[color:var(--ink-body)]">{p.excerpt}</p>
                    <span className="mt-auto inline-flex items-center gap-1 pt-5 text-sm text-[color:var(--evergreen)]">
                      Read <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
